// src/RoleManager.js
import React, { useEffect, useState } from 'react';
import api from './apiClient'; // Use centralized client
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack'; // For feedback toasts

const ROLE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

function RoleManager({
  open,
  onClose,
  workspaceId,
  workspaceName,
  currentUserId,
  workspaceOwnerId,
}) {
  const [users, setUsers] = useState([]);
  const [rolesByUser, setRolesByUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open && workspaceId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [usersRes, rolesRes] = await Promise.all([
        api.get('/api/workspace-users', { params: { workspace_id: workspaceId } }),
        api.get('/api/roles', { params: { workplace_id: workspaceId } }),
      ]);

      const usersData = usersRes.data || [];
      const rolesData = rolesRes.data || [];

      const map = {};
      rolesData.forEach((r) => {
        if (r.user_id && r.role) {
          map[r.user_id] = r.role;
        }
      });

      setUsers(usersData);
      setRolesByUser(map);
    } catch (err) {
      console.error('Error loading role manager data:', err);
      setError(err.response?.data?.error || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    // Optional confirmation
    if (!window.confirm(`Set role to ${newRole || 'None'} for this user?`)) return;

    try {
      setRolesByUser((prev) => ({ ...prev, [userId]: newRole }));

      await api.post('/api/roles', {
        user_id: userId,
        workplace_id: workspaceId,
        role: newRole || null,
      });
      enqueueSnackbar('Role updated successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error updating role:', err);
      enqueueSnackbar(err.response?.data?.error || 'Error updating role', { variant: 'error' });
      // Revert on error
      loadData(); // Reload to sync
    }
  };

  const handleClose = () => {
    onClose();
  };

  const sortedUsers = [...users].sort((a, b) =>
    String(a.username).toLowerCase().localeCompare(String(b.username).toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Roles –{' '}
        <Typography component="span" color="primary">
          {workspaceName || 'Workspace'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Workspace owner and admins can control who can view or edit items in
          this workspace.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell align="center">Role</TableCell>
                <TableCell align="center">Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => {
                const role = rolesByUser[user.id] || '';
                const isOwner = user.id === workspaceOwnerId;
                const isSelf = user.id === currentUserId;

                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {user.username}
                        </Typography>
                        {isSelf && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            (You)
                          </Typography>
                        )}
                        {isOwner && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          >
                            Owner
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {isOwner ? (
                        <Typography variant="body2">Owner</Typography>
                      ) : (
                        <Select
                          size="small"
                          value={role}
                          onChange={(e) =>
                            handleRoleChange(user.id, e.target.value)
                          }
                          displayEmpty
                          sx={{ minWidth: 130 }}
                          disabled={isSelf} // Optional: disable self-editing
                        >
                          {ROLE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {role === 'viewer' && (
                        <Typography variant="caption" color="text.secondary">
                          Can view only
                        </Typography>
                      )}
                      {role === 'editor' && (
                        <Typography variant="caption" color="text.secondary">
                          Can create & edit tasks
                        </Typography>
                      )}
                      {role === 'admin' && (
                        <Typography variant="caption" color="text.secondary">
                          Full access in workspace
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoleManager;